import React, { useState, useEffect } from 'react';

interface CarouselItem {
  type: 'image' | 'video';
  src: string;
}

interface CarouselProps {
  items: CarouselItem[];
  color?: string;
}

const Carousel = ({ items, color = 'rgb(252, 149, 46)' }: CarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPortrait, setIsPortrait] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };
    handleResize(); // Initial call
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
  };

  const renderItem = (item: CarouselItem) => {
    if (!item) return null;

    // Container relatif pour positionner le média en absolu
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        {item.type === 'image' ? (
          <img
            src={`https://studio.retorik.ai/mejstjegx80had7vep/media/${item.src}`}
            alt="Carousel item"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        ) : (
          // If the source is a direct video file (mp4, mpa) render a native
          // <video> element so fullscreen is available. Otherwise fall back to
          // the iframe embed.
          (() => {
            const srcLower = (item.src || '').toLowerCase();
            const isDirectVideo = srcLower.endsWith('.mp4') || srcLower.endsWith('.mpa') || srcLower.includes('.mp4?') || srcLower.includes('.mpa?');

            if (isDirectVideo) {
              return (
                <video
                  src={item.src}
                  controls
                  playsInline
                  // Prevent Picture-in-Picture in supporting browsers
                  disablePictureInPicture
                  // Allow native fullscreen via controls; remove download
                  controlsList="nodownload noplaybackrate"
                  // Prevent default context menu (three-dots) on long press/right click
                  onContextMenu={(e) => e.preventDefault()}
                  // If playback rate changes, reset to 1
                  onRateChange={(e) => {
                    const t = e.currentTarget as HTMLVideoElement;
                    if (t.playbackRate !== 1) t.playbackRate = 1;
                  }}
                  // Also prevent keyboard shortcuts from changing rate (some players may use +/-)
                  onKeyDown={(e) => {
                    // prevent +/- keys changing playback speed when video is focused
                    if (e.key === '+' || e.key === '-' || e.key === '=' ) {
                      e.preventDefault();
                    }
                  }}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    backgroundColor: '#000'
                  }}
                />
              );
            }

            // Fallback to iframe for embeds (Drive preview, etc.)
            return (
              <iframe
                src={item.src}
                title={`carousel-video`}
                allow="autoplay; encrypted-media"
                sandbox="allow-scripts allow-same-origin"
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: 'calc(100% + 80px)',
                  top: '-80px',
                  left: 0,
                  border: 'none',
                }}
              ></iframe>
            );
          })()
        )}
      </div>
    );
  };

  return (
    <div className="rf-relative rf-flex rf-flex-row vertical:rf-flex-col rf-h-full rf-overflow-hidden">
      {/* Viewport stable */}
      <div
        className="rf-relative rf-overflow-hidden rf-w-full"
        style={{
          // Hauteur stable : ratio ou clamp
          aspectRatio: '6/1',
          // Pour compatibilité ancienne : fallback clamp si aspectRatio pas supporté
          height: isPortrait ? '23vh' : '100%',
          backgroundColor: '#fff',
        }}
      >
        {renderItem(items[currentIndex])}
      </div>

      {/* Bullets */}
      <div className="rf-absolute rf-bottom-4 rf-left-1/2 -rf-translate-x-1/2 rf-flex rf-flex-row rf-items-center rf-gap-2">
        {items.map((_, index) => (
          <div
            key={index}
            className="rf-h-3 rf-w-3 rf-rounded-full rf-border rf-border-truewhite rf-cursor-pointer"
            onClick={() => setCurrentIndex(index)}
            style={{
              backgroundColor: currentIndex === index ? color : 'transparent',
            }}
          />
        ))}
      </div>

      {/* Flèche gauche */}
      <div
        className="rf-absolute rf-left-4 rf-top-1/2 -rf-translate-y-1/2 rf-cursor-pointer"
        onClick={goToPrevious}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          className="rf-h-6 large:rf-h-8"
        >
          <path
            d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"
            fill="black"
            stroke="white"
            strokeWidth="1"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {/* Flèche droite */}
      <div
        className="rf-absolute rf-right-4 rf-top-1/2 -rf-translate-y-1/2 rf-cursor-pointer"
        onClick={goToNext}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          className="rf-h-6 large:rf-h-8"
        >
          <path
            d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"
            fill="black"
            stroke="white"
            strokeWidth="1"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      </div>

    </div>
  );
};

export default Carousel;
